import { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Eraser, PenLine, Loader2 } from "lucide-react";
import { API, useAuth } from "../App";

/**
 * Self-service rúbrica editor opened from the employee profile.
 * Lets the employee draw (or redraw) their signature at any time.
 */
const SignatureEditDialog = ({ open, onOpenChange, hasExisting, onSaved }) => {
  const sigRef = useRef(null);
  const { token, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const reset = () => {
    sigRef.current?.clear();
    setHasDrawn(false);
    setAccepted(false);
  };

  const handleClear = () => {
    sigRef.current?.clear();
    setHasDrawn(false);
  };

  const handleSave = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error("Desenhe sua assinatura antes de salvar.");
      return;
    }
    if (!accepted) {
      toast.error("Você precisa concordar com os termos antes de salvar.");
      return;
    }
    setSaving(true);
    try {
      const dataUrl = sigRef.current.toDataURL("image/png");
      await axios.post(
        `${API}/employee/signature`,
        { signature_base64: dataUrl },
        { params: { token } },
      );
      updateUser({ has_signature: true });
      toast.success(
        hasExisting
          ? "Rúbrica atualizada com sucesso!"
          : "Rúbrica cadastrada com sucesso!",
      );
      reset();
      onSaved?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Erro ao salvar a rúbrica",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-xl" data-testid="signature-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <PenLine className="w-5 h-5 text-slate-700" />
            {hasExisting ? "Refazer rúbrica" : "Cadastrar rúbrica"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Desenhe sua rúbrica abaixo. Ela será usada como sua assinatura
            digital nos documentos da AVI Segurança.
            {hasExisting && " A rúbrica atual será substituída."}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-2">
          <div className="bg-white rounded-md border border-dashed border-slate-300 relative">
            <SignatureCanvas
              ref={sigRef}
              penColor="#0f172a"
              onEnd={() => setHasDrawn(true)}
              canvasProps={{
                className: "w-full h-44 sm:h-52 rounded-md touch-none",
                "data-testid": "signature-edit-canvas",
              }}
            />
            {!hasDrawn && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-slate-300">
                <div className="flex flex-col items-center gap-1">
                  <PenLine className="w-6 h-6" />
                  <span className="text-xs">Desenhe sua assinatura aqui</span>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between px-1 pt-2">
            <span className="text-xs text-slate-400">
              Use o dedo (touch) ou o mouse para desenhar.
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              disabled={saving}
              className="text-slate-600 hover:text-slate-900 h-8"
              data-testid="signature-edit-clear-btn"
            >
              <Eraser className="w-4 h-4 mr-1" />
              Limpar
            </Button>
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1 w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
            data-testid="signature-edit-accept-checkbox"
          />
          <span>
            Declaro que esta é minha assinatura e autorizo seu uso como
            rúbrica digital nos documentos da AVI Segurança.
          </span>
        </label>

        <div className="flex flex-col sm:flex-row gap-2 pt-1">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="w-full sm:flex-1"
            data-testid="signature-edit-cancel-btn"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !hasDrawn || !accepted}
            className="w-full sm:flex-1 bg-slate-900 hover:bg-slate-800"
            data-testid="signature-edit-save-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando…
              </>
            ) : hasExisting ? (
              "Substituir rúbrica"
            ) : (
              "Salvar rúbrica"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SignatureEditDialog;
